import StaffProfileFields from '../../components/StaffProfileFields';
import type { StaffProfile } from '../../components/StaffProfiles';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getUserInfo, setUserInfo } from '../../api/client';
import { useTheme, Theme } from '../../context/ThemeContext';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import NotificationPreferences, { type NotificationPrefsValue } from '../../components/preferences/NotificationPreferences';
import SettingsCard from '../../components/SettingsCard';
import SearchableSelect from '../../components/SearchableSelect';
import PageHeader from '../../components/PageHeader';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IUserPreferences {
  themeColor: string;
  suppressNotifications?: boolean;
  eventReminders?: 'off' | 'special' | 'all';
  notifications?: NotificationPrefsValue;
  dismissedOnboardingBanner?: boolean;
}

const AdminPreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [staffProfile, setStaffProfile] = useState<StaffProfile>({ aboutMe: '', photoUrl: null });
  const [photoBusy, setPhotoBusy] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [themeColor, setThemeColor] = useState('#ffffff');
  // Legacy kill switch, honoured until the matrix below is saved once.
  const [legacySuppressed, setLegacySuppressed] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefsValue | undefined>(undefined);
  const [dismissedOnboardingBanner, setDismissedOnboardingBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.profile.get();
        const profileData = data as { preferences?: IUserPreferences; staffProfile?: StaffProfile };
        setStaffProfile(profileData.staffProfile ?? { aboutMe: '', photoUrl: null });
        setProfileReady(true);

        if (profileData.preferences) {
          setThemeColor(profileData.preferences.themeColor || '#ffffff');
          setLegacySuppressed(profileData.preferences.suppressNotifications || false);
          setNotifPrefs(
            profileData.preferences.notifications ?? {
              v: 1,
              channels: {},
              types: {},
              eventRemindersScope: profileData.preferences.eventReminders ?? 'all',
            },
          );
          setDismissedOnboardingBanner(profileData.preferences.dismissedOnboardingBanner || false);
        }
      } catch (err: any) {
         setError(translateError(err, t));
      }
    };
    fetchProfile();
  }, [navigate]);

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (photoBusy || !profileReady) return;
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const { data } = await api.profile.update({
        staffProfile,
        preferences: {
          theme,
          themeColor,
          notifications: notifPrefs,
          dismissedOnboardingBanner,
        },
      } as any);

      setLegacySuppressed(false);

      // Update local storage
      const userInfo = getUserInfo();
      if (userInfo && data) {
        const updatedUserInfo = { ...userInfo, ...(data as object) };
        setUserInfo(updatedUserInfo as any);
      }

      setMessage(t('preferences.updateSuccess'));
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  };

  return (
    <div className="admin-page-content preferences-page">
      <PageHeader title={t('preferences.title')} />
      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <SettingsCard title={t('staffProfile.title')} actions={<Button type="submit" form="admin-prefs-form" loading={loading} disabled={photoBusy || !profileReady}>{t('common.save')}</Button>}>
        <StaffProfileFields value={staffProfile} onChange={setStaffProfile} onBusy={setPhotoBusy} disabled={loading || !profileReady} />
      </SettingsCard>
      <div className="settings-grid settings-grid--split">
        <SettingsCard
          title={t('preferences.sectionAppearance')}
          description={t('preferences.sectionAppearanceDesc')}
          actions={
            <Button type="submit" form="admin-prefs-form" loading={loading} disabled={photoBusy || !profileReady}>
              {loading ? t('common.updating') : t('common.save')}
            </Button>
          }
        >
          <FormField htmlFor="theme" label={t('preferences.theme')}>
            <SearchableSelect
              id="theme"
              value={theme}
              allowClear={false}
              options={[
                { _id: 'dark', name: t('preferences.themeDark') },
                { _id: 'light', name: t('preferences.themeLight') },
              ]}
              onChange={(value) => {
                if (value != null) setTheme(value as Theme);
              }}
            />
          </FormField>

          <FormField htmlFor="themeColor" label={t('preferences.themeColor')}>
            <div className="preferences-page__colorRow">
              <input
                type="color"
                id="themeColor"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                className="preferences-page__colorInput"
              />
              <span>{themeColor}</span>
            </div>
          </FormField>

          {dismissedOnboardingBanner && (
            <div className="form-group">
              <ToggleSwitch
                checked={!dismissedOnboardingBanner}
                onChange={() => setDismissedOnboardingBanner(false)}
                label={<span>{t('onboarding.reEnable')}</span>}
              />
            </div>
          )}
        </SettingsCard>

        <SettingsCard
          title={t('notificationPrefs.title')}
          description={t('notificationPrefs.description')}
          actions={
            <Button type="submit" form="admin-prefs-form" loading={loading} disabled={photoBusy || !profileReady}>
              {loading ? t('common.updating') : t('common.save')}
            </Button>
          }
        >
          <NotificationPreferences
            value={notifPrefs}
            legacySuppressed={legacySuppressed}
            onChange={setNotifPrefs}
          />
        </SettingsCard>
      </div>

      {/* One form behind both cards; `form=` wires each Save to this handler. */}
      <form id="admin-prefs-form" onSubmit={submitHandler} hidden />
    </div>
  );
};

export default AdminPreferencesPage;
